import { useEffect, useRef } from 'react'

import PropTypes from 'prop-types'
import dayjs from 'dayjs'

import { useSimpleState } from '../../hooks/useSimpleState'

import './index.css'

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const groupByMonth = (entries) => {
    let months = []
    let byMonth = {}

    entries.forEach((entry) => {
        let key = dayjs(entry.date).format('YYYY-MM')

        if (!byMonth[key]) {
            byMonth[key] = {}
            months.push(key)
        }

        byMonth[key][dayjs(entry.date).date()] = entry
    })

    return { months, byMonth }
}

// full calendar weeks (Sun-Sat) covering the active month, so the grid
// reads as an actual calendar — every day of the month gets a cell,
// with deal count inside it, blank cells outside the month pad the
// first/last week to a full 7 columns
const buildWeeks = (monthKey, byDay) => {
    let firstOfMonth = dayjs(`${monthKey}-01`)
    let daysInMonth = firstOfMonth.daysInMonth()
    let leadingBlanks = firstOfMonth.day()

    let cells = []

    for (let i = 0; i < leadingBlanks; i++)
        cells.push(null)

    for (let day = 1; day <= daysInMonth; day++) {
        let entry = byDay[day]

        cells.push({
            day,
            date: firstOfMonth.date(day).format('YYYY-MM-DD'),
            count: entry?.count || 0,
            hasData: !!entry
        })
    }

    while (cells.length % 7 !== 0)
        cells.push(null)

    let weeks = []

    for (let i = 0; i < cells.length; i += 7)
        weeks.push(cells.slice(i, i + 7))

    return weeks
}

// years descending, each with its own list of months descending —
// what the "Older" popover renders as a grid under each year heading
const groupByYear = (monthKeys) => {
    let years = []
    let byYear = {}

    monthKeys.forEach((month) => {
        let year = month.slice(0, 4)

        if (!byYear[year]) {
            byYear[year] = []
            years.push(year)
        }

        byYear[year].push(month)
    })

    return { years, byYear }
}

const DateScrubberComponent = ({ value=[], config={}, events={} }) => {
    const {_state} = useSimpleState({
        expanded: false,
        activeDate: null,
        activeMonth: null,
        showOlderMonths: false
    })

    const containerRef = useRef(null)

    const handleToggle = () => {
        let next = !_state.get('expanded')

        _state.set('expanded', next)

        if (next && events.handleExpand)
            events.handleExpand()
    }

    useEffect(() => {
        if (!_state.get('expanded'))
            return

        const collapse = () => _state.set('expanded', false)

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target))
                collapse()
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        window.addEventListener('scroll', collapse, { passive: true })

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
            window.removeEventListener('scroll', collapse)
        }
    }, [_state.get('expanded')])

    const commitDate = (date) => {
        if (!date)
            return

        _state.set('activeDate', date)

        if (events.handleSelect)
            events.handleSelect(date)
    }

    if (!value.length)
        return null

    const { months, byMonth } = groupByMonth(value)
    const activeMonth = _state.get('activeMonth') || months[0]
    const pinnedMonths = months.slice(0, 3)
    const otherMonths = months.slice(3)
    const activeMonthIsOther = otherMonths.includes(activeMonth)
    const { years: olderYears, byYear: olderByYear } = groupByYear(otherMonths)
    const weeks = buildWeeks(activeMonth, byMonth[activeMonth] || {})
    const activeDate = _state.get('activeDate') || config.currentDate

    const selectMonth = (month) => {
        _state.set('activeMonth', month)
        _state.set('showOlderMonths', false)
    }

    // months is newest-first, so "next" (more recent) steps the index
    // down and "prev" (older) steps it up — only enabled when there's
    // actually a month in that direction to land on
    const activeMonthIndex = months.indexOf(activeMonth)
    const hasPrevMonth = activeMonthIndex < months.length - 1
    const hasNextMonth = activeMonthIndex > 0

    const goToAdjacentMonth = (offset) => {
        let month = months[activeMonthIndex + offset]

        if (month)
            selectMonth(month)
    }

    const goToToday = () => {
        let todayMonth = dayjs().format('YYYY-MM')
        let todayDate = dayjs().format('YYYY-MM-DD')

        selectMonth(todayMonth)

        // only commit (and notify the parent to scroll/jump) when
        // today's actually got data — otherwise just land on the
        // current month with nothing selected
        if (byMonth[todayMonth]?.[dayjs().date()])
            commitDate(todayDate)
    }

    return (
        <div className={`c-DateScrubber${_state.get('expanded') ? ' is-expanded' : ''}`} ref={containerRef}>
            {!_state.get('expanded') ? (
                <button
                    type="button"
                    className="dateScrubber-pill"
                    onClick={handleToggle}
                >
                    <svg className="dateScrubber-pillIcon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" stroke="#fff" strokeWidth="1.3" />
                        <path d="M1.5 5.8H14.5" stroke="#fff" strokeWidth="1.3" />
                        <path d="M4.5 1.3V4" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
                        <path d="M11.5 1.3V4" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
                        <rect x="4" y="8" width="1.6" height="1.6" rx="0.3" fill="#fff" />
                        <rect x="7.2" y="8" width="1.6" height="1.6" rx="0.3" fill="#fff" />
                        <rect x="10.4" y="8" width="1.6" height="1.6" rx="0.3" fill="#fff" />
                        <rect x="4" y="10.8" width="1.6" height="1.6" rx="0.3" fill="#fff" />
                    </svg>
                    <span className="dateScrubber-pillLabel">Jump to Date</span>
                </button>
            ) : (
                <div className="dateScrubber-bar">
                    <div className="dateScrubber-months">
                        {pinnedMonths.map((month) => (
                            <button
                                type="button"
                                key={`dateScrubber-month-${month}`}
                                className={`dateScrubber-month${month === activeMonth ? ' is-active' : ''}`}
                                onClick={() => selectMonth(month)}
                            >
                                {dayjs(month).format('MMM YYYY')}
                            </button>
                        ))}

                        {otherMonths.length > 0 ? (
                            <button
                                type="button"
                                className={`dateScrubber-olderButton${_state.get('showOlderMonths') || activeMonthIsOther ? ' is-active' : ''}`}
                                onClick={() => _state.set('showOlderMonths', !_state.get('showOlderMonths'))}
                            >
                                Older
                            </button>
                        ) : null}

                        <button
                            type="button"
                            className="dateScrubber-todayButton"
                            onClick={goToToday}
                        >
                            Today
                        </button>
                    </div>

                    {_state.get('showOlderMonths') ? (
                        /* month-picker view — replaces the calendar in the
                           same space rather than overlaying it, so picking
                           an older month gets the same amount of room the
                           day grid normally has. Months wrap to fill the
                           row under each year, same idea as the day cells
                           below, styled distinctly (pills, not squares) so
                           the two views read as clearly different modes */
                        <div className="dateScrubber-yearPicker">
                            {olderYears.map((year) => (
                                <div className="dateScrubber-pickerYear" key={`dateScrubber-year-${year}`}>
                                    <div className="dateScrubber-pickerYearLabel">{year}</div>

                                    <div className="dateScrubber-pickerMonths">
                                        {olderByYear[year].map((month) => (
                                            <button
                                                type="button"
                                                key={`dateScrubber-older-${month}`}
                                                className={`dateScrubber-pickerMonth${month === activeMonth ? ' is-active' : ''}`}
                                                onClick={() => selectMonth(month)}
                                            >
                                                {dayjs(month).format('MMM')}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            {/* prev/next steps one calendar month at a time
                                without needing to reopen the picker view */}
                            <div className="dateScrubber-monthNav">
                                <button
                                    type="button"
                                    className="dateScrubber-monthNavButton"
                                    onClick={() => goToAdjacentMonth(1)}
                                    disabled={!hasPrevMonth}
                                    aria-label="Previous month"
                                >
                                    ‹
                                </button>

                                <span className="dateScrubber-monthNavLabel">{dayjs(activeMonth).format('MMMM YYYY')}</span>

                                <button
                                    type="button"
                                    className="dateScrubber-monthNavButton"
                                    onClick={() => goToAdjacentMonth(-1)}
                                    disabled={!hasNextMonth}
                                    aria-label="Next month"
                                >
                                    ›
                                </button>
                            </div>

                            {/* real calendar layout — weekday header so users
                                know which day of the week a column is, then
                                one row per week with the deal count shown
                                inside each cell */}
                            <div className="dateScrubber-weekdays">
                                {WEEKDAY_LABELS.map((label) => (
                                    <span className="dateScrubber-weekdayLabel" key={`dateScrubber-weekday-${label}`}>{label}</span>
                                ))}
                            </div>

                            <div className="dateScrubber-calendar">
                                {weeks.map((week, weekIndex) => (
                                    <div className="dateScrubber-week" key={`dateScrubber-week-${weekIndex}`}>
                                        {week.map((cell, cellIndex) => {
                                            if (!cell)
                                                return <div className="dateScrubber-cell is-blank" key={`dateScrubber-cell-${weekIndex}-${cellIndex}`} />

                                            return (
                                                <div
                                                    key={`dateScrubber-cell-${cell.date}`}
                                                    className={`dateScrubber-cell${cell.hasData ? ' has-data' : ''}${cell.date === activeDate ? ' is-active' : ''}`}
                                                    onClick={cell.hasData ? () => commitDate(cell.date) : undefined}
                                                >
                                                    <span className="dateScrubber-cellDay">{cell.day}</span>
                                                    {cell.hasData ? <span className="dateScrubber-cellCount">{cell.count}</span> : null}
                                                </div>
                                            )
                                        })}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

DateScrubberComponent.propTypes = {
    value: PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.string.isRequired,
        count: PropTypes.number
    })),
    config: PropTypes.shape({
        currentDate: PropTypes.string
    }),
    events: PropTypes.shape({
        handleSelect: PropTypes.func,
        handleExpand: PropTypes.func
    })
}

export { DateScrubberComponent }
